import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, PrimaryDialog, SearchBox, Typography } from "@foodpilot/foods";
import { Box, Divider, Stack, useTheme } from "@mui/material";
import { SupplierFormAppearance, SupplierFormIngredientSupplier } from "../../../SupplierForm.types";
import { SupplierIngredientsGrid } from "./SupplierIngredientsGrid";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { EntityQuitConfirmDialog } from "src/components/EntityForm";
import { ReferenceSearchDialog } from "src/components/ReferenceSearch";
import { IngredientSupplierTransportFields } from "src/components/Transport/IngredientSupplierTransportFields";
import type {
    IngredientSupplierTransportChangeReason,
    IngredientSupplierTransportSelection,
} from "src/components/Transport/ingredientSupplierTransportTypes";
import { SupplierService } from "src/pages/Supplier/SupplierService";

type SupplierIngredientsModalProps = {
    appearance?: SupplierFormAppearance;
    existingIngredientSuppliers: SupplierFormIngredientSupplier[];
    isOpen: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    onValidate: (newIngredientSuppliers: SupplierFormIngredientSupplier[]) => void;
};
export const SupplierIngredientsModal = (props: SupplierIngredientsModalProps): ReactNode | null => {
    if (!props.isOpen) {
        return null;
    }

    return <OpenSupplierIngredientsModal {...props} />;
};

const OpenSupplierIngredientsModal = (props: SupplierIngredientsModalProps): ReactNode => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { appearance = "legacy", existingIngredientSuppliers, setOpen, onValidate } = props;
    const search = useDelayedSearch();

    const [selectedIngredientSuppliers, setSelectedIngredientsSuppliers] = useState(existingIngredientSuppliers);
    const [confirmRemovalOpen, setConfirmRemovalOpen] = useState(false);

    useEffect(() => {
        // The dialog owns a cancelable draft and must reset it when its source changes.
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
        setSelectedIngredientsSuppliers(existingIngredientSuppliers);
    }, [existingIngredientSuppliers]);

    const finishClose = () => {
        setConfirmRemovalOpen(false);
        search.resetSearch();
        setOpen(false);
    };

    const cancel = () => {
        setSelectedIngredientsSuppliers(existingIngredientSuppliers);
        finishClose();
    };

    const commitDraft = () => {
        onValidate(selectedIngredientSuppliers);
        finishClose();
    };

    const allOriginalIds = existingIngredientSuppliers.map((i) => i.ingredient["@id"]);
    const allNewlySelectedIds = selectedIngredientSuppliers.map((i) => i.ingredient["@id"]);
    const shouldConfirmOnClose = shouldConfirmAction(allOriginalIds, allNewlySelectedIds);

    const validate = () => {
        if (shouldConfirmOnClose) {
            setConfirmRemovalOpen(true);
            return;
        }

        commitDraft();
    };

    const ingredients = (
        <DisplayIngredients
            appearance={appearance}
            currentIngredientSuppliers={selectedIngredientSuppliers}
            delayedSearch={search.delayedSearch}
            setNewIngredientSuppliers={setSelectedIngredientsSuppliers}
        />
    );

    if (appearance === "legacy") {
        return (
            <PrimaryDialog
                maxWidth="lg"
                fullWidth
                open
                setOpen={setOpen}
                options={{
                    title: <Typography variant="h2">{t("ajouter_ou_dissocier_des_ingredients")}</Typography>,
                    content: (
                        <Box sx={{ minHeight: theme.spacing(25), overflowX: "hidden" }}>
                            <Stack direction="row" justifyContent="space-between" sx={{ padding: theme.spacing(3) }}>
                                <SearchBox
                                    variant="standalone"
                                    value={search.search}
                                    onChange={search.setSearch}
                                    placeholder={t("rechercher_un_ingredient")}
                                />
                            </Stack>
                            {ingredients}
                        </Box>
                    ),
                }}
                onValidate={commitDraft}
                onCancel={cancel}
                cancelText={t("primarydialog.cancel")}
                validateText={t("primarydialog.validate")}
                confirmAction={
                    shouldConfirmOnClose ?
                        {
                            title: (
                                <Typography variant="h3">{t("suppliers.edit.confirmdeleteingsup.title")}</Typography>
                            ),
                            content: (
                                <Typography variant="body-medium">
                                    {t("suppliers.edit.confirmdeleteingsup.message")}
                                </Typography>
                            ),
                            cancelText: t("suppliers.edit.confirmdeleteingsup.cancel"),
                            validateText: t("suppliers.edit.confirmdeleteingsup.confirm"),
                        }
                    :   undefined
                }
            />
        );
    }

    return (
        <>
            <ReferenceSearchDialog
                closeLabel={t("common.close", "Close")}
                onClose={cancel}
                onSearchChange={search.setSearch}
                open
                searchPlaceholder={t("rechercher_un_ingredient")}
                searchValue={search.search}
                footer={
                    <Stack
                        alignItems="center"
                        direction="row"
                        flexShrink={0}
                        gap={theme.spacing(1)}
                        height="100%"
                        justifyContent="flex-end"
                        paddingX={theme.spacing(2)}
                        sx={{
                            borderTop: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                            boxSizing: "border-box",
                        }}
                    >
                        <Button variant="text" onClick={cancel}>
                            {t("primarydialog.cancel")}
                        </Button>
                        <Button variant="primary" onClick={validate}>
                            {t("primarydialog.validate")}
                        </Button>
                    </Stack>
                }
                title={t("ajouter_ou_dissocier_des_ingredients")}
            >
                {ingredients}
            </ReferenceSearchDialog>

            <EntityQuitConfirmDialog
                open={confirmRemovalOpen}
                title={t("suppliers.edit.confirmdeleteingsup.title")}
                content={t("suppliers.edit.confirmdeleteingsup.message")}
                cancelLabel={t("suppliers.edit.confirmdeleteingsup.cancel")}
                confirmLabel={t("suppliers.edit.confirmdeleteingsup.confirm")}
                onCancel={() => setConfirmRemovalOpen(false)}
                onConfirm={commitDraft}
            />
        </>
    );
};

const shouldConfirmAction = (originalIds: IRI[], newIds: IRI[]) => {
    if (originalIds.length === 0) return false;

    for (const key of originalIds) {
        const doesKeyExist = newIds.includes(key);
        if (doesKeyExist === false) {
            return true;
        }
    }

    return false;
};

type DisplayIngredientsProps = {
    appearance: SupplierFormAppearance;
    currentIngredientSuppliers: SupplierFormIngredientSupplier[];
    delayedSearch: string;
    setNewIngredientSuppliers: Dispatch<SetStateAction<SupplierFormIngredientSupplier[]>>;
};
const DisplayIngredients = (props: DisplayIngredientsProps) => {
    const theme = useTheme();
    const { appearance, currentIngredientSuppliers, delayedSearch, setNewIngredientSuppliers } = props;

    const updateTransportSelections = (
        ingredientIri: IRI,
        transportSelections: IngredientSupplierTransportSelection,
        reason: IngredientSupplierTransportChangeReason,
    ) => {
        setNewIngredientSuppliers((current) =>
            SupplierService.updateTransportSelections(current, ingredientIri, transportSelections, reason),
        );
    };

    return (
        <Box
            component="div"
            sx={{
                flex: "1 1 auto",
                minHeight: 0,
                overflowX: "hidden",
                overflowY: "auto",
            }}
        >
            <SupplierIngredientsGrid
                appearance={appearance}
                existingIngredientSuppliers={currentIngredientSuppliers}
                setNewIngredientSuppliers={setNewIngredientSuppliers}
                delayedSearch={delayedSearch}
            />
            {currentIngredientSuppliers.length > 0 && (
                <Stack gap={theme.spacing(2)} sx={{ padding: theme.spacing(3) }}>
                    {appearance === "legacy" ?
                        <Divider />
                    :   <Box
                            aria-hidden
                            sx={{
                                borderTopColor: theme.custom.strokes.containerDivider.color,
                                borderTopStyle: theme.custom.strokes.containerDivider.style,
                                borderTopWidth: theme.custom.strokes.containerDivider.width,
                            }}
                        />
                    }
                    {currentIngredientSuppliers.map((ingredientSupplier) => (
                        <IngredientSupplierTransportFields
                            appearance={appearance}
                            key={ingredientSupplier.ingredient["@id"]}
                            ingredientSupplierId={extractIdFromIri(ingredientSupplier["@id"])}
                            value={ingredientSupplier.transportSelections}
                            onChange={(next, reason) =>
                                updateTransportSelections(ingredientSupplier.ingredient["@id"], next, reason)
                            }
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
};

const extractIdFromIri = (iri?: IRI): number | undefined => {
    if (iri === undefined) {
        return undefined;
    }

    const rawId = iri.split("/").pop();
    const id = rawId === undefined ? NaN : Number.parseInt(rawId, 10);

    return Number.isNaN(id) ? undefined : id;
};
